import React, { useEffect, useRef, useState } from 'react';
import "../assets/css/LazyImage.css"

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  srcSet?: string;
  sizes?: string;
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className,
  placeholder,
  srcSet,
  sizes
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const imgElement = imgRef.current;
    if (!imgElement) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              imgElement.src = src;
              if (srcSet) imgElement.srcset = srcSet;
              observer.unobserve(imgElement);
            }
          });
        },
        {
          rootMargin: '100px',
          threshold: 0.01,
        }
      );

      observer.observe(imgElement);

      return () => observer.unobserve(imgElement);
    } else {
      // Fallback for browsers without IntersectionObserver
      imgElement.src = src;
      if (srcSet) imgElement.srcset = srcSet;
    }
  }, [src, srcSet]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div className="lazy-image-wrapper">
      <img
        ref={imgRef}
        alt={alt}
        className={`lazy-image ${className || ''} ${isLoaded ? 'loaded' : ''}`}
        data-src={src}
        sizes={sizes}
        onLoad={handleLoad}
      />
      {!isLoaded && (
        <div className="lazy-image-loading">
          {placeholder && <div className="lazy-image-placeholder" style={{ backgroundImage: `url(${placeholder})` }} />}
          <div className="lazy-image-spinner" />
        </div>
      )}
    </div>
  );
};

export default LazyImage;
